'use client';

import { c as _c } from "react/compiler-runtime";
import type { HofDashboardEntry } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { HOF_ENTRY_STATUS_LABELS, HOF_REVIEW_CHOICES, type HofEntryStatus } from '@/features/hof-dashboard/constants';
import { useAutosaveReview, type AutosaveState } from '@/features/hof-dashboard/hooks/use-autosave-review';
import { formatDateTime, translate, type TextKey } from '@/features/hof-dashboard/texts';
import AccordionItem from '@/features/payload-cms/components/accordion/accordion-item';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { Radio, RadioGroup } from '@headlessui/react';
import { Check, CloudOff } from 'lucide-react';
import type React from 'react';
import { useId, useState } from 'react';

/** Every change of status and feedback, newest first, folded away until asked for. */
const ReviewHistory: React.FC<{
  entry: HofDashboardEntry;
  locale: Locale;
}> = t0 => {
  const $ = _c(15);
  const {
    entry,
    locale
  } = t0;
  const [open, setOpen] = useState(false);
  const historyId = useId();
  let t1;
  if ($[0] !== entry.reviewLog.length || $[1] !== locale) {
    t1 = translate("reviewHistory", locale, {
      n: entry.reviewLog.length
    });
    $[0] = entry.reviewLog.length;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="text-sm font-semibold text-gray-700">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(_temp);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== entry.reviewLog || $[7] !== locale || $[8] !== open) {
    t4 = open && <ol className="space-y-3">{entry.reviewLog.map((change, index) => <li key={`${change.at}-${index}`} className="text-sm"><p className="text-xs text-gray-500">{formatDateTime(change.at, locale)} ·{" "}{change.by === "" ? translate("reviewerUnknown", locale) : change.by}</p><p className="font-semibold text-gray-900">{HOF_ENTRY_STATUS_LABELS[change.status][locale]}{change.final && ` · ${translate("finalShort", locale)}`}</p>{change.feedback !== "" && <p className="whitespace-pre-line text-gray-700">{change.feedback}</p>}</li>)}</ol>;
    $[6] = entry.reviewLog;
    $[7] = locale;
    $[8] = open;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== historyId || $[11] !== open || $[12] !== t2 || $[13] !== t4) {
    t5 = <AccordionItem titleElement={t2} showChevron={true} accordionId={historyId} isExpanded={open} onToggle={t3} isNested={true}>{t4}</AccordionItem>;
    $[10] = historyId;
    $[11] = open;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};

/** "Eingereicht" first: no status of the Ressort yet, the way a new submission starts. */
const CHOICES: HofEntryStatus[] = ['submitted', ...HOF_REVIEW_CHOICES];
const AUTOSAVE_LABEL: Record<Exclude<AutosaveState, 'idle'>, TextKey> = {
  typing: 'autosaveTyping',
  saving: 'saving',
  saved: 'saved',
  offline: 'autosaveOffline',
  error: 'autosaveError'
};

/** Where the autosave stands, as a small pill beside the title; nothing before the first edit. */
const AutosavePill: React.FC<{
  state: AutosaveState;
  locale: Locale;
  onRetry: () => void;
}> = t0 => {
  const $ = _c(22);
  const {
    state,
    locale,
    onRetry
  } = t0;
  if (state === "idle") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span aria-live="polite" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const failed = state === "error" || state === "offline";
  const t1 = failed ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-gray-100 text-gray-600 ring-gray-200";
  let t2;
  if ($[1] !== t1) {
    t2 = cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset", t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== state) {
    t3 = state === "saved" && <Check className="h-3.5 w-3.5" aria-hidden={true} />;
    $[3] = state;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== failed) {
    t4 = failed && <CloudOff className="h-3.5 w-3.5" aria-hidden={true} />;
    $[5] = failed;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = AUTOSAVE_LABEL[state];
  let t6;
  if ($[7] !== locale || $[8] !== t5) {
    t6 = translate(t5, locale);
    $[7] = locale;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t6) {
    t7 = <span className={t2}>{t3}{t4}{t6}</span>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== locale || $[16] !== onRetry || $[17] !== state) {
    t8 = state === "error" && <button type="button" onClick={onRetry} className="text-conveniat-green min-h-10 cursor-pointer text-xs font-semibold hover:underline">{translate("retry", locale)}</button>;
    $[15] = locale;
    $[16] = onRetry;
    $[17] = state;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <span aria-live="polite" className="flex items-center gap-2">{t7}{t8}</span>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};

/**
 * Where a reviewer answers a submission: its status, as the option cards of the site's forms,
 * and the feedback the Hof reads next to it. Saved as it is given, without a button: a status
 * at once, the feedback once typing pauses.
 */
export const ReviewPanel: React.FC<{
  entry: HofDashboardEntry;
  hofId: string;
  /** For the version that counts of a form of versions: it can be made the last one. */
  canFinalize: boolean;
  locale: Locale;
}> = t0 => {
  const $ = _c(66);
  const {
    entry,
    hofId,
    canFinalize,
    locale
  } = t0;
  const t1 = entry.feedback ?? "";
  let t2;
  if ($[0] !== entry.final || $[1] !== entry.reviewStatus || $[2] !== t1) {
    t2 = {
      status: entry.reviewStatus,
      feedback: t1,
      final: entry.final
    };
    $[0] = entry.final;
    $[1] = entry.reviewStatus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    values,
    setStatus,
    setFeedback,
    setFinal,
    state,
    retry
  } = useAutosaveReview(hofId, entry.id, t2);
  const finalId = useId();
  const statusId = useId();
  const feedbackId = useId();
  let t3;
  if ($[4] !== locale) {
    t3 = translate("reviewTitle", locale);
    $[4] = locale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <p className="text-sm font-semibold text-gray-900">{t3}</p>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== locale || $[9] !== retry || $[10] !== state) {
    t5 = <AutosavePill state={state} locale={locale} onRetry={retry} />;
    $[8] = locale;
    $[9] = retry;
    $[10] = state;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex flex-wrap items-center justify-between gap-2">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = values.status ?? "submitted";
  let t8;
  if ($[15] !== setStatus) {
    t8 = choice => setStatus(choice === "submitted" ? undefined : choice);
    $[15] = setStatus;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== locale) {
    t9 = translate("reviewStatus", locale);
    $[17] = locale;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== statusId || $[20] !== t9) {
    t10 = <p id={statusId} className="font-body mb-2 text-sm font-medium text-gray-500">{t9}</p>;
    $[19] = statusId;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== locale) {
    t11 = CHOICES.map(choice_0 => <Radio key={choice_0} value={choice_0} className="font-body flex min-h-10 cursor-pointer items-center justify-center rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-center text-sm font-medium text-gray-700 transition-all duration-200 hover:border-gray-300 data-checked:border-green-600 data-checked:bg-green-50 data-checked:text-green-700 data-focus:ring-2 data-focus:ring-green-600 data-focus:ring-offset-2 data-focus:outline-none">{HOF_ENTRY_STATUS_LABELS[choice_0][locale]}</Radio>);
    $[22] = locale;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <div className="grid grid-cols-2 gap-2 @3xl:grid-cols-4">{t11}</div>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== statusId || $[27] !== t10 || $[28] !== t12 || $[29] !== t7 || $[30] !== t8) {
    t13 = <RadioGroup value={t7} onChange={t8} aria-labelledby={statusId}>{t10}{t12}</RadioGroup>;
    $[26] = statusId;
    $[27] = t10;
    $[28] = t12;
    $[29] = t7;
    $[30] = t8;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== locale) {
    t14 = translate("reviewFeedback", locale);
    $[32] = locale;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== feedbackId || $[35] !== t14) {
    t15 = <label htmlFor={feedbackId} className="font-body mb-1 block text-sm font-medium text-gray-500">{t14}</label>;
    $[34] = feedbackId;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== setFeedback) {
    t16 = event => setFeedback(event.target.value);
    $[37] = setFeedback;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== feedbackId || $[40] !== t16 || $[41] !== values.feedback) {
    t17 = <textarea id={feedbackId} rows={3} maxLength={5000} value={values.feedback} onChange={t16} className="focus:ring-conveniat-green font-body min-h-[88px] w-full rounded-md border-0 bg-green-100 px-4 py-2 text-base text-gray-600 shadow-sm ring-1 ring-transparent transition-all duration-200 ring-inset placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:outline-none focus:ring-inset" />;
    $[39] = feedbackId;
    $[40] = t16;
    $[41] = values.feedback;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== locale) {
    t18 = translate("reviewHint", locale);
    $[43] = locale;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t18) {
    t19 = <p className="mt-1 text-xs text-gray-500">{t18}</p>;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t15 || $[48] !== t17 || $[49] !== t19) {
    t20 = <div>{t15}{t17}{t19}</div>;
    $[47] = t15;
    $[48] = t17;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== canFinalize || $[52] !== finalId || $[53] !== locale || $[54] !== setFinal || $[55] !== values.final) {
    t21 = canFinalize && <div className="flex items-start gap-3"><input id={finalId} type="checkbox" checked={values.final} onChange={event_0 => setFinal(event_0.target.checked)} className="text-conveniat-green focus:ring-conveniat-green mt-[3px] h-4 w-4 rounded border-0 bg-green-100 shadow-sm ring-1 ring-transparent ring-inset focus:ring-2 focus:ring-offset-0 focus:outline-none" /><label htmlFor={finalId} className="font-body text-sm text-gray-700"><span className="font-medium text-gray-900">{translate("finalLabel", locale)}</span><span className="block text-xs text-gray-500">{translate("finalHint", locale)}</span></label></div>;
    $[51] = canFinalize;
    $[52] = finalId;
    $[53] = locale;
    $[54] = setFinal;
    $[55] = values.final;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== entry || $[58] !== locale) {
    t22 = entry.reviewLog.length > 0 && <ReviewHistory entry={entry} locale={locale} />;
    $[57] = entry;
    $[58] = locale;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t13 || $[61] !== t20 || $[62] !== t21 || $[63] !== t22 || $[64] !== t6) {
    t23 = <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">{t6}{t13}{t20}{t21}{t22}</div>;
    $[60] = t13;
    $[61] = t20;
    $[62] = t21;
    $[63] = t22;
    $[64] = t6;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  return t23;
};
function _temp(shown) {
  return !shown;
}